// src/useMcpChat.ts
import { useState, useEffect, useCallback, useRef, useMemo } from "react";

// src/mcpClient.ts
class McpClient {
  config;
  requestIdCounter = 0;
  currentRequestController;
  constructor(config) {
    this.config = {
      baseUrl: config.baseUrl,
      authToken: config.authToken ?? "",
      headers: config.headers ?? {},
      timeout: config.timeout ?? 30000
    };
  }
  generateRequestId() {
    return `${Date.now()}-${++this.requestIdCounter}`;
  }
  buildHeaders() {
    const headers = {
      "Content-Type": "application/json",
      ...this.config.headers
    };
    if (this.config.authToken) {
      headers["Authorization"] = `Bearer ${this.config.authToken}`;
    }
    return headers;
  }
  async executeRequest(method, params, signal) {
    const request = {
      jsonrpc: "2.0",
      id: this.generateRequestId(),
      method,
      params
    };
    console.log(`\uD83D\uDCE1 MCP Request [${method}]`);
    const timeoutController = new AbortController;
    const timeoutId = setTimeout(() => timeoutController.abort(), this.config.timeout);
    const requestController = new AbortController;
    this.currentRequestController = requestController;
    const combinedSignal = this.combineAbortSignals([
      timeoutController.signal,
      requestController.signal,
      ...signal ? [signal] : []
    ]);
    try {
      const response = await fetch(this.config.baseUrl, {
        method: "POST",
        headers: this.buildHeaders(),
        body: JSON.stringify(request),
        signal: combinedSignal
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      if (data.error) {
        throw new Error(`MCP Error [${data.error.code}]: ${data.error.message}`);
      }
      if (!data.result) {
        throw new Error("MCP response missing result");
      }
      return data.result;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "AbortError") {
          throw new Error("Request timeout or cancelled");
        }
        throw error;
      }
      throw new Error("Unknown error during MCP request");
    } finally {
      clearTimeout(timeoutId);
      if (this.currentRequestController === requestController) {
        this.currentRequestController = undefined;
      }
    }
  }
  combineAbortSignals(signals) {
    const controller = new AbortController;
    for (const signal of signals) {
      if (signal.aborted) {
        controller.abort();
        break;
      }
      signal.addEventListener("abort", () => controller.abort(), { once: true });
    }
    return controller.signal;
  }
  async initialize(signal) {
    return this.executeRequest("initialize", {
      protocolVersion: "2025-06-18",
      capabilities: {},
      clientInfo: {
        name: "use-hapimcp",
        version: "0.1.0"
      }
    }, signal);
  }
  async listTools(signal) {
    const result = await this.executeRequest("tools/list", {}, signal);
    return result.tools;
  }
  async callTool(name, args, signal) {
    return this.executeRequest("tools/call", { name, arguments: args }, signal);
  }
  async listResources(signal) {
    try {
      const result = await this.executeRequest("resources/list", {}, signal);
      return result.resources;
    } catch (error) {
      console.warn("Resources list not supported by server:", error);
      return [];
    }
  }
  async ping(signal) {
    try {
      await this.executeRequest("ping", {}, signal);
      return true;
    } catch {
      return false;
    }
  }
  cancel() {
    if (this.currentRequestController) {
      this.currentRequestController.abort();
      this.currentRequestController = undefined;
    }
  }
  updateConfig(config) {
    this.config = {
      ...this.config,
      ...config
    };
  }
}

// src/agentEngine.ts
class AgentEngine {
  rules;
  actionLogs = [];
  constructor(rules = []) {
    this.rules = [...rules].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }
  evaluate(action) {
    if (this.rules.length === 0) {
      return {
        status: "allowed",
        reason: "No rules defined, permissive mode"
      };
    }
    for (const rule of this.rules) {
      if (this.matchesRule(action, rule)) {
        const status = this.ruleTypeToStatus(rule.type);
        return {
          status,
          matchedRule: rule,
          reason: rule.description
        };
      }
    }
    return {
      status: "blocked",
      reason: "No matching rule found, default deny"
    };
  }
  matchesRule(action, rule) {
    const pattern = rule.pattern;
    if (typeof pattern === "string") {
      return action.name.toLowerCase().includes(pattern.toLowerCase());
    }
    if (pattern instanceof RegExp) {
      return pattern.test(action.name);
    }
    return false;
  }
  ruleTypeToStatus(ruleType) {
    switch (ruleType) {
      case "allow":
        return "allowed";
      case "deny":
        return "blocked";
      case "require-approval":
        return "needs-approval";
      default:
        return "blocked";
    }
  }
  log(action, status, result, error, matchedRule, duration) {
    const actionLog = {
      id: this.generateLogId(),
      action,
      status,
      ruleMatch: matchedRule,
      result,
      error,
      timestamp: new Date().toISOString(),
      duration
    };
    this.actionLogs.push(actionLog);
    return actionLog;
  }
  generateLogId() {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
  getLogs() {
    return [...this.actionLogs];
  }
  getLogsByStatus(status) {
    return this.actionLogs.filter((log) => log.status === status);
  }
  getLogsByActionType(type) {
    return this.actionLogs.filter((log) => log.action.type === type);
  }
  clearLogs() {
    this.actionLogs = [];
  }
  updateRules(rules) {
    this.rules = [...rules].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }
  getRules() {
    return [...this.rules];
  }
  addRule(rule) {
    this.rules.push(rule);
    this.rules.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }
  removeRule(ruleId) {
    const initialLength = this.rules.length;
    this.rules = this.rules.filter((r) => r.id !== ruleId);
    return this.rules.length < initialLength;
  }
  updateActionStatus(logId, newStatus) {
    const log = this.actionLogs.find((l) => l.id === logId);
    if (log) {
      log.status = newStatus;
      return true;
    }
    return false;
  }
}
var RulePatterns = {
  allowTools(pattern, description) {
    return {
      id: `allow-tools-${Date.now()}`,
      type: "allow",
      pattern,
      description: description ?? `Allow tools matching: ${pattern}`,
      priority: 1
    };
  },
  denyTools(pattern, description) {
    return {
      id: `deny-tools-${Date.now()}`,
      type: "deny",
      pattern,
      description: description ?? `Deny tools matching: ${pattern}`,
      priority: 10
    };
  },
  requireApproval(pattern, description) {
    return {
      id: `approval-tools-${Date.now()}`,
      type: "require-approval",
      pattern,
      description: description ?? `Require approval for tools matching: ${pattern}`,
      priority: 5
    };
  },
  allowAll() {
    return {
      id: "allow-all",
      type: "allow",
      pattern: /.*/,
      description: "Allow all actions",
      priority: 0
    };
  },
  denyAll() {
    return {
      id: "deny-all",
      type: "deny",
      pattern: /.*/,
      description: "Deny all actions",
      priority: 0
    };
  }
};

// src/llmRegistry.ts
function convertToolToOpenAiFormat(tool) {
  return {
    type: "function",
    function: {
      name: tool.name,
      description: tool.description,
      parameters: tool.inputSchema
    }
  };
}
var OllamaProvider = {
  id: "ollama",
  name: "Ollama",
  async sendChat({ messages, tools, toolChoice, config, signal, metadata }) {
    const url = `${config.baseUrl}/chat/completions`;
    const requestBody = {
      model: config.model,
      messages: messages.map((msg) => ({
        role: msg.role === "tool" ? "assistant" : msg.role,
        content: msg.content,
        ...msg.toolCalls && { tool_calls: msg.toolCalls }
      })),
      temperature: config.temperature ?? 0.7,
      max_tokens: config.maxTokens ?? 2000
    };
    if (tools && tools.length > 0) {
      requestBody.tools = tools.map(convertToolToOpenAiFormat);
      if (toolChoice) {
        requestBody.tool_choice = toolChoice;
      }
    }
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...config.headers
      },
      body: JSON.stringify(requestBody),
      signal
    });
    if (!response.ok) {
      throw new Error(`Ollama Error: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    return {
      choices: data.choices.map((choice) => ({
        message: {
          role: choice.message.role,
          content: choice.message.content || "",
          toolCalls: choice.message.tool_calls?.map((tc) => ({
            id: tc.id,
            name: tc.function.name,
            arguments: JSON.parse(tc.function.arguments || "{}")
          }))
        },
        finishReason: choice.finish_reason
      })),
      usage: data.usage && {
        promptTokens: data.usage.prompt_tokens,
        completionTokens: data.usage.completion_tokens,
        totalTokens: data.usage.total_tokens
      }
    };
  }
};
var OpenAIProvider = {
  id: "openai",
  name: "OpenAI",
  async sendChat({ messages, tools, toolChoice, config, signal, metadata }) {
    const url = `${config.baseUrl}/chat/completions`;
    const requestBody = {
      model: config.model,
      messages: messages.map((msg) => ({
        role: msg.role === "tool" ? "tool" : msg.role,
        content: msg.content,
        ...msg.toolCalls && {
          tool_calls: msg.toolCalls.map((tc) => ({
            id: tc.id,
            type: "function",
            function: {
              name: tc.name,
              arguments: JSON.stringify(tc.arguments)
            }
          }))
        }
      })),
      temperature: config.temperature ?? 0.7,
      max_tokens: config.maxTokens ?? 2000
    };
    if (tools && tools.length > 0) {
      requestBody.tools = tools.map(convertToolToOpenAiFormat);
      if (toolChoice) {
        requestBody.tool_choice = toolChoice;
      }
    }
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${config.apiKey}`,
        ...config.headers
      },
      body: JSON.stringify(requestBody),
      signal
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`OpenAI Error: ${response.status} ${errorData.error?.message || response.statusText}`);
    }
    const data = await response.json();
    return {
      choices: data.choices.map((choice) => ({
        message: {
          role: choice.message.role,
          content: choice.message.content || "",
          toolCalls: choice.message.tool_calls?.map((tc) => ({
            id: tc.id,
            name: tc.function.name,
            arguments: JSON.parse(tc.function.arguments || "{}")
          }))
        },
        finishReason: choice.finish_reason
      })),
      usage: data.usage && {
        promptTokens: data.usage.prompt_tokens,
        completionTokens: data.usage.completion_tokens,
        totalTokens: data.usage.total_tokens
      }
    };
  }
};
var AnthropicProvider = {
  id: "anthropic",
  name: "Anthropic",
  async sendChat({ messages, tools, toolChoice, config, signal, metadata }) {
    const url = `${config.baseUrl}/messages`;
    const systemMessages = messages.filter((m) => m.role === "system");
    const nonSystemMessages = messages.filter((m) => m.role !== "system");
    const requestBody = {
      model: config.model,
      max_tokens: config.maxTokens ?? 2000,
      messages: nonSystemMessages.map((msg) => ({
        role: msg.role === "assistant" ? "assistant" : "user",
        content: msg.content
      })),
      temperature: config.temperature ?? 0.7
    };
    if (systemMessages.length > 0) {
      requestBody.system = systemMessages.map((m) => m.content).join(`
`);
    }
    if (tools && tools.length > 0) {
      requestBody.tools = tools.map((tool) => ({
        name: tool.name,
        description: tool.description,
        input_schema: tool.inputSchema
      }));
    }
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": config.apiKey || "",
        "anthropic-version": "2023-06-01",
        ...config.headers
      },
      body: JSON.stringify(requestBody),
      signal
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Anthropic Error: ${response.status} ${errorData.error?.message || response.statusText}`);
    }
    const data = await response.json();
    const content = data.content[0]?.text || "";
    const toolCalls = data.content.filter((block) => block.type === "tool_use").map((block) => ({
      id: block.id,
      name: block.name,
      arguments: block.input
    }));
    return {
      choices: [
        {
          message: {
            role: "assistant",
            content,
            toolCalls: toolCalls.length > 0 ? toolCalls : undefined
          },
          finishReason: data.stop_reason
        }
      ],
      usage: data.usage && {
        promptTokens: data.usage.input_tokens,
        completionTokens: data.usage.output_tokens,
        totalTokens: data.usage.input_tokens + data.usage.output_tokens
      }
    };
  }
};
var defaultLlmRegistry = {
  ollama: OllamaProvider,
  openai: OpenAIProvider,
  anthropic: AnthropicProvider
};
function createLlmRegistry(providers) {
  return { ...providers };
}
function getProvider(registry, llmId, fallbackId) {
  const provider = registry[llmId] || (fallbackId ? registry[fallbackId] : undefined);
  if (!provider) {
    throw new Error(`LLM provider '${llmId}' not found in registry`);
  }
  return provider;
}

// src/sessionStore.ts
class LocalStorageSessionStore {
  prefix;
  constructor(prefix = "mcp-session") {
    this.prefix = prefix;
  }
  getKey(sessionId) {
    return `${this.prefix}:${sessionId}`;
  }
  async load(sessionId) {
    try {
      const key = this.getKey(sessionId);
      const data = localStorage.getItem(key);
      if (!data) {
        return null;
      }
      const state = JSON.parse(data);
      if (!state.sessionId || !Array.isArray(state.messages)) {
        console.warn("Invalid session state structure, returning null");
        return null;
      }
      return state;
    } catch (error) {
      console.error("Failed to load session:", error);
      return null;
    }
  }
  async save(sessionId, state) {
    try {
      const key = this.getKey(sessionId);
      const data = JSON.stringify(state);
      localStorage.setItem(key, data);
    } catch (error) {
      console.error("Failed to save session:", error);
      throw new Error("Session save failed");
    }
  }
  async clear(sessionId) {
    try {
      const key = this.getKey(sessionId);
      localStorage.removeItem(key);
    } catch (error) {
      console.error("Failed to clear session:", error);
      throw new Error("Session clear failed");
    }
  }
  async listSessions() {
    try {
      const sessions = [];
      const prefixWithColon = `${this.prefix}:`;
      for (let i = 0;i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefixWithColon)) {
          sessions.push(key.substring(prefixWithColon.length));
        }
      }
      return sessions;
    } catch (error) {
      console.error("Failed to list sessions:", error);
      return [];
    }
  }
}

class InMemorySessionStore {
  storage = new Map;
  async load(sessionId) {
    return this.storage.get(sessionId) ?? null;
  }
  async save(sessionId, state) {
    this.storage.set(sessionId, state);
  }
  async clear(sessionId) {
    this.storage.delete(sessionId);
  }
  clearAll() {
    this.storage.clear();
  }
  getSessions() {
    return Array.from(this.storage.keys());
  }
}
function createDefaultSessionStore() {
  if (typeof globalThis !== "undefined" && typeof globalThis.localStorage !== "undefined") {
    return new LocalStorageSessionStore;
  }
  console.warn("localStorage not available, using in-memory session store");
  return new InMemorySessionStore;
}
function createInitialSessionState(sessionId, llmId, rules = []) {
  return {
    sessionId,
    messages: [],
    tools: [],
    resources: [],
    llmId,
    rules,
    actions: [],
    updatedAt: new Date().toISOString(),
    metadata: {}
  };
}

// src/useMcpChat.ts
function useMcpChat(config) {
  const defaultStore = useMemo(() => createDefaultSessionStore(), []);
  const {
    sessionId,
    mcp,
    llmProviders,
    initialLlmId,
    llmConfigs,
    store = defaultStore,
    initialRules = [],
    onAction,
    onError
  } = config;
  const [status, setStatus] = useState("idle");
  const [messages, setMessages] = useState([]);
  const [tools, setTools] = useState([]);
  const [resources, setResources] = useState([]);
  const [actions, setActions] = useState([]);
  const [llmId, setLlmId] = useState(initialLlmId);
  const [rules, setRules] = useState(initialRules);
  const [error, setError] = useState();
  const mcpClientRef = useRef(null);
  const agentEngineRef = useRef(new AgentEngine(initialRules));
  const abortControllerRef = useRef(null);
  const isInitializedRef = useRef(false);
  const isInitializingRef = useRef(false);
  const hasInitFailedRef = useRef(false);
  const lastInitSignatureRef = useRef(null);
  const normalizeBaseUrl = useCallback((url) => url.replace(/\/+$/, ""), []);
  const mcpBaseUrl = useMemo(() => normalizeBaseUrl(mcp.baseUrl), [mcp.baseUrl, normalizeBaseUrl]);
  const toInitError = useCallback((err) => {
    if (err instanceof Error) {
      const message = err.message || "Initialization failed";
      if (message === "Failed to fetch" || /cors/i.test(message)) {
        return new Error(`Failed to reach MCP server at ${mcpBaseUrl}. This often indicates a CORS or network issue. Original error: ${message}`);
      }
      return new Error(message);
    }
    return new Error("Initialization failed");
  }, [mcpBaseUrl]);
  const initialize = useCallback(async () => {
    const signature = `${sessionId}|${mcpBaseUrl}|${mcp.authToken ?? ""}`;
    if (isInitializingRef.current)
      return;
    if (signature === lastInitSignatureRef.current && (isInitializedRef.current || hasInitFailedRef.current))
      return;
    isInitializingRef.current = true;
    lastInitSignatureRef.current = signature;
    hasInitFailedRef.current = false;
    try {
      setStatus("connecting");
      setError(undefined);
      mcpClientRef.current = new McpClient({ ...mcp, baseUrl: mcpBaseUrl });
      const persistedState = await store.load(sessionId);
      if (persistedState) {
        setMessages(persistedState.messages);
        setTools(persistedState.tools);
        setResources(persistedState.resources || []);
        setLlmId(persistedState.llmId);
        setRules(persistedState.rules);
        setActions(persistedState.actions);
        agentEngineRef.current.updateRules(persistedState.rules);
      }
      await mcpClientRef.current.initialize();
      const [fetchedTools, fetchedResources] = await Promise.all([
        mcpClientRef.current.listTools(),
        mcpClientRef.current.listResources().catch(() => [])
      ]);
      setTools(fetchedTools);
      setResources(fetchedResources);
      await persistState({
        messages: persistedState?.messages || [],
        tools: fetchedTools,
        resources: fetchedResources,
        llmId: persistedState?.llmId || initialLlmId,
        rules: persistedState?.rules || initialRules,
        actions: persistedState?.actions || []
      });
      setStatus("ready");
      isInitializedRef.current = true;
    } catch (err) {
      hasInitFailedRef.current = true;
      const error2 = toInitError(err);
      setError(error2);
      setStatus("error");
      onError?.(error2);
    } finally {
      isInitializingRef.current = false;
    }
  }, [
    sessionId,
    mcpBaseUrl,
    mcp.authToken,
    JSON.stringify(mcp.headers ?? {}),
    store,
    initialLlmId,
    initialRules,
    onError,
    toInitError
  ]);
  const persistState = useCallback(async (updates) => {
    const state = createInitialSessionState(sessionId, llmId, rules);
    state.messages = updates.messages ?? messages;
    state.tools = updates.tools ?? tools;
    state.resources = updates.resources ?? resources;
    state.llmId = updates.llmId ?? llmId;
    state.rules = updates.rules ?? rules;
    state.actions = updates.actions ?? actions;
    state.updatedAt = new Date().toISOString();
    await store.save(sessionId, state);
  }, [sessionId, messages, tools, resources, llmId, rules, actions, store]);
  const resolveLlmConfig = useCallback(() => {
    const providerConfig = llmConfigs?.[llmId];
    if (providerConfig) {
      return {
        ...providerConfig,
        model: providerConfig.model ?? "default",
        baseUrl: normalizeBaseUrl(providerConfig.baseUrl),
        headers: { ...mcp.headers ?? {}, ...providerConfig.headers ?? {} }
      };
    }
    return {
      baseUrl: mcpBaseUrl,
      model: "default",
      headers: mcp.headers,
      apiKey: mcp.authToken
    };
  }, [llmConfigs, llmId, mcp.headers, mcp.authToken, mcpBaseUrl, normalizeBaseUrl]);
  const generateMessageId = useCallback(() => {
    return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }, []);
  const executeToolCall = useCallback(async (toolCall) => {
    const startTime = Date.now();
    const action = {
      type: "tool-call",
      name: toolCall.name,
      input: toolCall.arguments
    };
    const evaluation = agentEngineRef.current.evaluate(action);
    if (evaluation.status === "blocked") {
      const error2 = `Tool '${toolCall.name}' blocked by rule: ${evaluation.reason}`;
      const actionLog = agentEngineRef.current.log(action, "blocked", undefined, error2, evaluation.matchedRule, Date.now() - startTime);
      setActions((prev) => [...prev, actionLog]);
      onAction?.(actionLog);
      return {
        toolCallId: toolCall.id,
        name: toolCall.name,
        content: error2,
        isError: true
      };
    }
    if (evaluation.status === "needs-approval") {
      const actionLog = agentEngineRef.current.log(action, "needs-approval", undefined, undefined, evaluation.matchedRule, 0);
      setActions((prev) => [...prev, actionLog]);
      onAction?.(actionLog);
      return {
        toolCallId: toolCall.id,
        name: toolCall.name,
        content: `Tool '${toolCall.name}' requires approval. Reason: ${evaluation.reason}`,
        isError: false
      };
    }
    try {
      const result = await mcpClientRef.current.callTool(toolCall.name, toolCall.arguments, abortControllerRef.current?.signal);
      const duration = Date.now() - startTime;
      const content = result.content.map((c) => c.text || JSON.stringify(c.data)).join(`
`);
      const actionLog = agentEngineRef.current.log(action, "allowed", result, undefined, evaluation.matchedRule, duration);
      setActions((prev) => [...prev, actionLog]);
      onAction?.(actionLog);
      return {
        toolCallId: toolCall.id,
        name: toolCall.name,
        content,
        isError: result.isError
      };
    } catch (err) {
      const duration = Date.now() - startTime;
      const errorMessage = err instanceof Error ? err.message : "Tool execution failed";
      const actionLog = agentEngineRef.current.log(action, "allowed", undefined, errorMessage, evaluation.matchedRule, duration);
      setActions((prev) => [...prev, actionLog]);
      onAction?.(actionLog);
      return {
        toolCallId: toolCall.id,
        name: toolCall.name,
        content: errorMessage,
        isError: true
      };
    }
  }, [onAction]);
  const sendUserMessage = useCallback(async (content, options) => {
    if (status !== "ready") {
      throw new Error("MCP client not ready");
    }
    try {
      setStatus("sending");
      setError(undefined);
      abortControllerRef.current = new AbortController;
      const userMessage = {
        id: generateMessageId(),
        role: "user",
        content,
        timestamp: new Date().toISOString(),
        metadata: options?.metadata
      };
      const updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);
      const provider = getProvider(llmProviders, llmId);
      const llmConfig = resolveLlmConfig();
      await persistState({ messages: updatedMessages });
      let response = await provider.sendChat({
        messages: updatedMessages,
        tools: tools.length > 0 ? tools : undefined,
        toolChoice: tools.length > 0 ? "auto" : undefined,
        config: llmConfig,
        signal: abortControllerRef.current.signal
      });
      let assistantMessage = response.choices[0]?.message;
      let iterationCount = 0;
      const maxIterations = 5;
      while (assistantMessage && assistantMessage.toolCalls && iterationCount < maxIterations) {
        iterationCount++;
        const toolResults = await Promise.all(assistantMessage.toolCalls.map((tc) => executeToolCall(tc)));
        const assistantMsgWithTools = {
          id: generateMessageId(),
          role: "assistant",
          content: assistantMessage.content || "",
          toolCalls: assistantMessage.toolCalls,
          timestamp: new Date().toISOString()
        };
        const toolMessages = toolResults.map((result) => ({
          id: generateMessageId(),
          role: "tool",
          content: typeof result.content === "string" ? result.content : JSON.stringify(result.content),
          toolResults: [result],
          timestamp: new Date().toISOString()
        }));
        updatedMessages.push(assistantMsgWithTools, ...toolMessages);
        setMessages([...updatedMessages]);
        response = await provider.sendChat({
          messages: updatedMessages,
          tools: tools.length > 0 ? tools : undefined,
          toolChoice: "auto",
          config: llmConfig,
          signal: abortControllerRef.current.signal
        });
        assistantMessage = response.choices[0]?.message;
      }
      const finalMessage = {
        id: generateMessageId(),
        role: "assistant",
        content: assistantMessage?.content || "",
        timestamp: new Date().toISOString()
      };
      updatedMessages.push(finalMessage);
      setMessages(updatedMessages);
      await persistState({ messages: updatedMessages });
      setStatus("ready");
    } catch (err) {
      const error2 = err instanceof Error ? err : new Error("Send message failed");
      setError(error2);
      setStatus("error");
      onError?.(error2);
      throw error2;
    } finally {
      abortControllerRef.current = null;
    }
  }, [
    status,
    messages,
    tools,
    llmId,
    llmProviders,
    generateMessageId,
    executeToolCall,
    persistState,
    resolveLlmConfig,
    onError
  ]);
  const refreshTools = useCallback(async () => {
    if (!mcpClientRef.current)
      return;
    try {
      const fetchedTools = await mcpClientRef.current.listTools();
      setTools(fetchedTools);
      await persistState({ tools: fetchedTools });
    } catch (err) {
      const error2 = err instanceof Error ? err : new Error("Refresh tools failed");
      setError(error2);
      onError?.(error2);
    }
  }, [persistState, onError]);
  const retry = useCallback(async () => {
    if (status !== "error")
      return;
    isInitializedRef.current = false;
    hasInitFailedRef.current = false;
    lastInitSignatureRef.current = null;
    await initialize();
  }, [status, initialize]);
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (mcpClientRef.current) {
      mcpClientRef.current.cancel();
    }
    setStatus((current) => current === "sending" ? "ready" : current);
  }, []);
  const reset = useCallback(async () => {
    setMessages([]);
    setTools([]);
    setResources([]);
    setActions([]);
    agentEngineRef.current.clearLogs();
    isInitializedRef.current = false;
    hasInitFailedRef.current = false;
    lastInitSignatureRef.current = null;
    await store.clear(sessionId);
    await initialize();
  }, [sessionId, store, initialize]);
  const updateRules = useCallback((newRules) => {
    setRules(newRules);
    agentEngineRef.current.updateRules(newRules);
    persistState({ rules: newRules }).catch((err) => {
      const error2 = err instanceof Error ? err : new Error("Failed to persist rules");
      setError(error2);
      onError?.(error2);
    });
  }, [persistState, onError]);
  const updateLlmId = useCallback((newLlmId) => {
    setLlmId(newLlmId);
    persistState({ llmId: newLlmId }).catch((err) => {
      const error2 = err instanceof Error ? err : new Error("Failed to persist LLM id");
      setError(error2);
      onError?.(error2);
    });
  }, [persistState, onError]);
  useEffect(() => {
    initialize();
  }, [initialize]);
  useEffect(() => {
    return () => {
      cancel();
      if (mcpClientRef.current) {
        mcpClientRef.current.cancel();
      }
    };
  }, [cancel]);
  return {
    status,
    messages,
    tools,
    resources,
    actions,
    llmId,
    rules,
    error,
    setLlmId: updateLlmId,
    setRules: updateRules,
    sendUserMessage,
    retry,
    cancel,
    reset,
    refreshTools
  };
}
export {
  useMcpChat,
  getProvider,
  defaultLlmRegistry,
  createLlmRegistry,
  createInitialSessionState,
  createDefaultSessionStore,
  RulePatterns,
  OpenAIProvider,
  OllamaProvider,
  McpClient,
  LocalStorageSessionStore,
  InMemorySessionStore,
  AnthropicProvider,
  AgentEngine
};
